import { AdminCohorts } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { AdminTableContext } from '../../AdminTable';
import { CohortForm, CohortFormData } from './CohortForm';

export const EditCohortModal = (props: { cohortId: number; onClose: () => void }) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { data: cohortData, isSuccess } = AdminCohorts.useGetCohortDetails(props.cohortId);
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const { ...functions } = useForm<CohortFormData>({
    values: {
      name: cohortData?.name || '',
      clientId: (cohortData?.clientId || 0).toString(),
      description: cohortData?.description,
      available: cohortData?.available || false,
      url: cohortData?.url,
      startDate: cohortData?.startDate ? new Date(cohortData.startDate) : null,
      endDate: cohortData?.endDate ? new Date(cohortData.endDate) : null,
      kickoffDate: cohortData?.kickoffDate ? new Date(cohortData.kickoffDate) : null,
      applicationStartDate:
        cohortData?.applicationStartDate ? new Date(cohortData.applicationStartDate) : null,
      applicationEndDate:
        cohortData?.applicationEndDate ? new Date(cohortData.applicationEndDate) : null,
      duration: cohortData?.duration ?? null,
      template_id: cohortData?.template?.id.toString() ?? null,
      timeCommitment: cohortData?.timeCommitment ?? null,
    },
  });
  const queryClient = useQueryClient();
  const updateCohortMutator = AdminCohorts.useUpdateCohortDetails({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => setModalStage('error'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...AdminCohorts.getGetAdminCohortsQueryKey({ ...adminQueryOptionsV2 })],
        });
        queryClient.invalidateQueries({
          queryKey: [...AdminCohorts.getGetCohortDetailsQueryKey(props.cohortId)],
        });
        setModalStage('success');
      },
    },
  });
  const onSubmit: SubmitHandler<CohortFormData> = (data) => {
    updateCohortMutator.mutate({
      cohortId: props.cohortId,
      data: {
        name: data.name,
        clientId: parseInt(data.clientId),
        description: data.description ?? null,
        available: data.available ?? false,
        url: data.url ?? null,
        kickoffDate: data.kickoffDate ? data.kickoffDate.toISOString() : null,
        startDate: data.startDate ? data.startDate.toISOString() : null,
        endDate: data.endDate ? data.endDate.toISOString() : null,
        applicationStartDate:
          data.applicationStartDate ? data.applicationStartDate.toISOString() : null,
        applicationEndDate: data.applicationEndDate ? data.applicationEndDate.toISOString() : null,
        duration: data.duration ?? null,
        templateId: data.template_id ?? null,
        timeCommitment: data.timeCommitment ?? null,
      },
    });
  };
  return (
    <AdminFormModal
      variant='full-screen'
      title='Edit Cohort'
      modalStage={modalStage}
      onClose={props.onClose}
      submitCTA={{ text: 'Update Cohort', callback: () => functions.handleSubmit(onSubmit)() }}
    >
      {isSuccess && <CohortForm {...functions} />}
    </AdminFormModal>
  );
};
